import React, { useEffect, useState, useMemo } from 'react';
import { useLocalStorage } from 'react-use-v13';
import moment from 'moment';
import _ from 'lodash';
import uuid from 'uuid';
import SAlert from 'react-s-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'design-system/product/theme';
import cs from 'classnames';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import SelectReviewLevel from 'corp/components/UI/SelectReviewLevel';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextInput,
  IconButton,
} from 'react-scale/components/design-system';
import { Label } from 'design-system/Form';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import DateRangePicker from 'design-system/DateRangePicker';
import SelectProject from 'corp/components/UI/SelectProject';
import Select, { SelectOption } from 'corp/components/UI/Select';
import { Section } from 'react-scale/containers/corp/backfills/task/components/Section';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { SubtaskListItem } from '@scale/shared/types/corp/task/taskListItem';
import { TaskType, SubtaskQuery as Query } from '@scale/shared-types';
import { isValidObjectId } from 'react-scale/components/design-system/ObjectIdInput/ObjectIdInput';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';

const LOCAL_STORAGE_KEY = 'taskActionsSubtaskQueries';

const description = `
* This section facilitates querying subtasks using the filters provided below
    * Priority objects and last touched by filers are comma delimited
    * Priority objects uses the label field found in the project ontology
* When querying FMV projects, use the load more button until it disappears to find all results
* Use the buttons on the right to save or view your saved queries
    * You can also add a new saved query with JSON using the add button in the saved queries windows (ensure the JSON is in the correct format)
    * Queries are saved in local browser data. Switching browsers, machines, or clearing data may result in losing or being unable to view your queries
* Annotation count is the number of annotations in the response for completed subtasks and number of annotations in the latest attempt for pending subtasks 
`;

interface StoredQuery {
  id: string;
  name: string;
  lastUsedTime: string;
  query: Query;
}

interface Filters {
  projects: SelectOption[];
  lastTouchedBy: string;
  pipelineLayers: string[];
  priorityObjects: string;
  priorityObjectsCount: number;
  annotationCount: number;
  status: string[];
  lastTouchedFrom: moment.Moment;
  lastTouchedTo: moment.Moment;
}

interface FetchSubtasks {
  subtasks: SubtaskListItem[];
  error: string | null;
  isLoading: boolean;
}

const taskStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' },
];

const defaultFetchSubtasks: FetchSubtasks = {
  subtasks: [],
  error: null,
  isLoading: false,
};

const defaultFilters: Filters = {
  projects: [],
  lastTouchedBy: '',
  pipelineLayers: [],
  priorityObjects: '',
  priorityObjectsCount: 0,
  annotationCount: 0,
  status: [],
  lastTouchedFrom: moment.utc().subtract(1, 'month').startOf('day'),
  lastTouchedTo: moment.utc().endOf('day'),
};

const queryToFilters = (query: Query): Filters =>
  _.defaults(
    {
      projects: query.projects.map(value => ({
        label: value,
        value,
      })),
      lastTouchedBy: query.lastTouchedBy?.join(', '),
      pipelineLayers: query.pipelineLayers?.map(value => ({ label: value, value })),
      priorityObjects: query.priorityObjects?.join(', '),
      priorityObjectsCount: query.priorityObjectsCount,
      annotationCount: query.annotationCount,
      status: query.status?.map(value => ({ label: value, value })),
      lastTouchedFrom: query.lastTouchedFrom && moment(query.lastTouchedFrom, moment.ISO_8601),
      lastTouchedTo: query.lastTouchedTo && moment(query.lastTouchedTo, moment.ISO_8601),
    },
    defaultFilters,
  );

const filtersToQuery = (newFilters: Filters): Query => ({
  projects: _.map(newFilters.projects, 'value'),
  lastTouchedBy: (() => {
    const users = _.flow(
      v => _.split(v, ','),
      v => _.map(v, _.trim),
      v => _.filter(v, isValidObjectId),
    )(newFilters.lastTouchedBy);
    return users.length ? users : undefined;
  })(),
  pipelineLayers: newFilters.pipelineLayers.length
    ? _.map(newFilters.pipelineLayers, 'value')
    : undefined,
  priorityObjects: (() => {
    const objects = _.flow(
      v => _.split(v, ','),
      v => _.map(v, _.trim),
    )(newFilters.priorityObjects);
    return objects.length && newFilters.priorityObjects ? objects : undefined;
  })(),
  priorityObjectsCount: newFilters.priorityObjectsCount || undefined,
  annotationCount: newFilters.annotationCount || undefined,
  status: newFilters.status.length ? _.map(newFilters.status, 'value') : undefined,
  lastTouchedFrom: newFilters.lastTouchedFrom && newFilters.lastTouchedFrom.toISOString(),
  lastTouchedTo: newFilters.lastTouchedTo && newFilters.lastTouchedTo.toISOString(),
});

const Loader = () => <h3>Loading ...</h3>;

const Error = ({ error }: { error: string }) => <h3>{error}</h3>;

interface QuerySubtasksProps {
  onChange: (tasks: SubtaskListItem[]) => void;
  taskType?: TaskType;
  disallowedTaskTypes?: TaskType[];
  setLoading: (loading: boolean) => void;
  warning?: string;
}

const QuerySubtasks: React.FC<QuerySubtasksProps> = ({
  onChange,
  taskType,
  disallowedTaskTypes,
  setLoading,
  warning = '',
}) => {
  const classes = useStyles();
  const [savedQueries, setSavedQueries] = useLocalStorage<StoredQuery[]>(LOCAL_STORAGE_KEY, []);
  const [showSaveQuery, setShowSaveQuery] = useState<boolean>(false);
  const [showSavedQueries, setShowSavedQueries] = useState<boolean>(false);
  const [cursor, setCursor] = useState<number>(0);
  const [fetchSubtasks, setFetchSubtasks] = useState<FetchSubtasks>(defaultFetchSubtasks);
  const [selectedTasks, setSelectedTasks] = useState<SubtaskListItem[]>(fetchSubtasks.subtasks);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const query = useMemo(() => filtersToQuery(filters), [filters]);

  const storeQuery = (name: string, createFilters?: Filters): StoredQuery | undefined => {
    const newFilters = createFilters || filters;
    if (!newFilters) return;

    const newQuery: StoredQuery = {
      id: uuid.v4(),
      name,
      lastUsedTime: moment().format('MM/DD/YY HH:mm'),
      query: filtersToQuery(newFilters),
    };

    setSavedQueries([...savedQueries, newQuery]);
    return newQuery;
  };

  useEffect(() => {
    onChange(selectedTasks);
  }, [onChange, selectedTasks]);

  useEffect(() => {
    setSelectedTasks(fetchSubtasks.subtasks);
  }, [fetchSubtasks.subtasks]);

  useEffect(() => {
    setLoading(fetchSubtasks.isLoading);
  }, [setLoading, fetchSubtasks.isLoading]);

  const handleChange = <K extends keyof Filters>(name: K, value: Filters[K]) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
  };

  const handleSearch = async (cursor?: number) => {
    if (!filters.projects.length) {
      alert('A project is required');
    } else {
      setFetchSubtasks({ ...fetchSubtasks, ...{ isLoading: true } });
      try {
        const { subtasks, cursor: newCursor } = await apiFetch(
          null,
          'corp-api/task-actions/subtask',
          {
            method: 'POST',
            body: { ...query, cursor },
          },
        );
        setCursor(newCursor !== cursor ? newCursor : 0);
        setFetchSubtasks({
          subtasks: !cursor ? subtasks : [...fetchSubtasks.subtasks, ...subtasks],
          error: null,
          isLoading: false,
        });
      } catch (e) {
        setFetchSubtasks({
          subtasks: [],
          error: e.toString(),
          isLoading: false,
        });
      }
    }
  };

  const handleClear = async () => {
    setFilters(defaultFilters);
  };

  return (
    <>
      <Section
        name={`Query Subtasks (${fetchSubtasks.subtasks ? fetchSubtasks.subtasks.length : 0})`}
      >
        <div className={classes.filterInfo}>
          <div>
            <SanitizedMarkdown text={description} />
          </div>
          <div className={classes.savingButtons}>
            <Tooltip title="View Saved Queries" arrow>
              <IconButton onClick={() => setShowSavedQueries(true)}>
                <FontAwesomeIcon icon="rectangle-history" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save Query" arrow>
              <IconButton
                disabled={!filters.projects.length}
                onClick={() => setShowSaveQuery(true)}
              >
                <FontAwesomeIcon icon="plus" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div>
          {fetchSubtasks.isLoading && <Loader />}
          {fetchSubtasks.error && <Error error={fetchSubtasks.error} />}
          <div>
            <section className={classes.warning}>
              {disallowedTaskTypes
                ? `Disallowed Task Types: ${disallowedTaskTypes.join(', ')}`
                : ''}
            </section>
            <section className={classes.warning}>{warning ? `Warning: ${warning}` : ''}</section>
            <section className={classes.threeColumns}>
              <section>
                <Label>Projects:</Label>
                <SelectProject
                  onManualIdInsert={newSelectedProjects => {
                    handleChange(
                      'projects',
                      _.uniqBy([...filters.projects, ...newSelectedProjects], 'value'),
                    );
                  }}
                  disabled={fetchSubtasks.isLoading}
                  value={filters.projects}
                  onChange={(value: SelectOption[]) => handleChange('projects', value)}
                  disallowedTaskTypes={disallowedTaskTypes}
                  taskType={taskType}
                  multi
                  width="auto"
                />
              </section>
              <section>
                <Label>Priority Objects:</Label>
                <TextInput
                  type="text"
                  placeholder="Priority object"
                  value={filters.priorityObjects}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('priorityObjects', e.target.value)
                  }
                  fullWidth
                  borderRadius="4px"
                  minHeight="36px"
                  padding="0px 10px"
                />
              </section>
              <section className={!filters.priorityObjects ? classes.disabled : ''}>
                <Label>Priotity Objects Count:</Label>
                <TextInput
                  type="number"
                  value={filters.priorityObjectsCount}
                  disabled={!filters.priorityObjects}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    /^\d*$/.test(e.target.value) &&
                    handleChange('priorityObjectsCount', parseInt(e.target.value))
                  }
                  fullWidth
                  borderRadius="4px"
                  minHeight="36px"
                  padding="0px 10px"
                />
              </section>
            </section>
            <section className={classes.threeColumns}>
              <section>
                <Label>Pipeline Layers (L0, L12, etc):</Label>
                <SelectReviewLevel
                  value={filters.pipelineLayers}
                  includeDeliverable
                  includeL11
                  onChange={(value: string[]) => handleChange('pipelineLayers', value)}
                  multi
                />
              </section>
              <section>
                <Label>Last Touched By (CorpID):</Label>
                <TextInput
                  type="text"
                  placeholder="User IDs"
                  value={filters.lastTouchedBy}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('lastTouchedBy', e.target.value)
                  }
                  fullWidth
                  borderRadius="4px"
                  minHeight="36px"
                  padding="0px 10px"
                />
              </section>
              <section>
                <Label>Total Number of Annotations:</Label>
                <TextInput
                  type="number"
                  value={filters.annotationCount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    /^\d*$/.test(e.target.value) &&
                    handleChange('annotationCount', parseInt(e.target.value))
                  }
                  fullWidth
                  borderRadius="4px"
                  minHeight="36px"
                  padding="0px 10px"
                />
              </section>
            </section>
            <section className={classes.threeColumns}>
              <section>
                <Label>Status:</Label>
                <Select
                  value={filters.status}
                  options={taskStatusOptions}
                  onChange={(value: string[]) => handleChange('status', value)}
                  multi
                />
              </section>
              <section>
                <Label>Last Touched Date:</Label>
                <DateRangePicker
                  to={filters.lastTouchedTo}
                  from={filters.lastTouchedFrom}
                  setTo={(value: moment.Moment) => handleChange('lastTouchedTo', value)}
                  setFrom={(value: moment.Moment) => handleChange('lastTouchedFrom', value)}
                />
              </section>
            </section>
            <section className={classes.searchButtons}>
              <Button
                disabled={fetchSubtasks.isLoading || !filters.projects.length}
                onClick={() => handleSearch()}
              >
                Search
              </Button>
              {!!cursor && (
                <Button disabled={fetchSubtasks.isLoading} onClick={() => handleSearch(cursor)}>
                  Load more...
                </Button>
              )}
              <Button onClick={() => handleClear()}>Clear</Button>
            </section>

            <h5>Subtasks found: {fetchSubtasks.subtasks.length}</h5>
          </div>
        </div>
      </Section>
      <SavedQueriesDialog
        showSavedQueries={showSavedQueries}
        setSavedQueries={(queries: StoredQuery[]) => setSavedQueries(queries)}
        hideDialog={() => setShowSavedQueries(false)}
        savedQueries={savedQueries}
        setFilters={(filters: Filters) => setFilters(filters)}
        storeQuery={storeQuery}
      />
      <SaveQueryDialog
        showSaveQuery={showSaveQuery}
        hideDialog={() => setShowSaveQuery(false)}
        storeQuery={storeQuery}
      />
    </>
  );
};

const useStyles = makeStyles(() => ({
  threeColumns: {
    display: 'flex',
    gap: '10px',
    '& section': {
      flexBasis: '33%',
    },
  },
  filterInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginTop: '-16px',
  },
  savingButtons: {
    display: 'flex',
  },
  disabled: {
    color: 'rgba(0, 0, 0, 0.3)',
  },
  warning: {
    fontWeight: 'bold',
    color: 'red',
  },
  searchButtons: {
    display: 'flex',
    gap: 10,
    paddingTop: 10,
  },
}));

interface SaveQueryDialogProps {
  showSaveQuery: boolean;
  hideDialog: () => void;
  storeQuery: (name: string, createFilters?: Filters) => StoredQuery | undefined;
}

const SaveQueryDialog = ({ showSaveQuery, hideDialog, storeQuery }: SaveQueryDialogProps) => {
  const [name, setName] = useState('');

  const handleCreateQuery = () => {
    storeQuery(name);
    hideDialog();
  };

  return (
    <Dialog open={showSaveQuery} onClose={hideDialog}>
      <DialogTitle>Save Query</DialogTitle>
      <DialogContent>
        <TextInput
          label="Query Name"
          size="large"
          type="string"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={hideDialog}>Cancel</Button>
        <Button disabled={!name} type="primary" onClick={handleCreateQuery}>
          Create Query
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface SavedQueriesDialogProps {
  setSavedQueries: (queries: StoredQuery[]) => void;
  showSavedQueries: boolean;
  hideDialog: () => void;
  savedQueries: StoredQuery[];
  setFilters: (filters: Filters) => void;
  storeQuery: (name: string, createFilters?: Filters) => StoredQuery | undefined;
}
const SavedQueriesDialog = ({
  setSavedQueries,
  showSavedQueries,
  hideDialog,
  savedQueries,
  setFilters,
  storeQuery,
}: SavedQueriesDialogProps) => {
  const classes = useSavedQueriesStyles();
  const [selectedQuery, setSelectedQuery] = useState<StoredQuery | null>(
    savedQueries.length ? savedQueries[0] : null,
  );
  const [newQuery, setNewQuery] = useState<{ name: string; query: string } | null>(null);

  const updateQueryLastUsedTime = (queryId: string) => {
    const newQueries = savedQueries.map(query =>
      query.id === queryId ? { ...query, lastUsedTime: moment().format('MM/DD/YY HH:mm') } : query,
    );
    setSavedQueries(newQueries);
  };

  const handleLoadQuery = () => {
    if (!selectedQuery) return;
    setFilters(queryToFilters(selectedQuery.query));
    updateQueryLastUsedTime(selectedQuery.id);
    hideDialog();
  };

  const handleDeleteQuery = () => {
    if (!selectedQuery) return;

    setSavedQueries(_.remove(savedQueries, q => q.id !== selectedQuery.id));
    setSelectedQuery(savedQueries.length ? savedQueries[0] : null);
  };

  const handleCreateQuery = () => {
    if (!newQuery) return;
    try {
      const jsonQuery = JSON.parse(newQuery.query);
      const filters = queryToFilters(jsonQuery);
      const query = storeQuery(newQuery.name, filters);
      if (!query) {
        SAlert.error('Unable to store queries at this time, please try again later');
        return;
      }
      setSelectedQuery(query);
      setNewQuery(null);
    } catch (e) {
      SAlert.error('Invalid query format provided. Please check your input and try again');
    }
  };

  return (
    <Dialog fullWidth open={showSavedQueries} onClose={hideDialog} disableEnforceFocus>
      <DialogTitle>Saved Queries</DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.queryListScrollbar}>
          {[...savedQueries].map(query => (
            <div
              onClick={() => {
                setSelectedQuery(query);
                setNewQuery(null);
              }}
              key={query.id}
              className={cs(
                classes.queryItem,
                query.id === selectedQuery?.id && classes.selectedItem,
              )}
            >
              <div className={classes.queryItemName}>{query.name}</div>
              <div className={classes.queryItemDate}>
                <span>Last Used: </span>
                <span>{query.lastUsedTime}</span>
              </div>
            </div>
          ))}
          {!newQuery ? (
            <IconButton
              className={classes.addQueryButton}
              onClick={() => {
                setNewQuery({ name: '', query: '' });
                setSelectedQuery(null);
              }}
            >
              <FontAwesomeIcon size="sm" icon="plus" />
            </IconButton>
          ) : (
            <div className={cs(classes.queryItem, classes.selectedItem)}>
              <TextInput
                type="text"
                placeholder="Query Name"
                value={newQuery.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewQuery({ query: newQuery.query, name: e.target.value })
                }
                fullWidth
              />
            </div>
          )}
        </div>
        <div className={classes.queryDataContainer}>
          {!!selectedQuery && !newQuery && (
            <ReactJsonView
              src={selectedQuery.query}
              name={false}
              theme="monokai"
              iconStyle="triangle"
              collapsed={1}
              collapseStringsAfterLength={120}
              enableClipboard={true}
              groupArraysAfterLength={false}
              displayObjectSize={false}
              displayDataTypes={false}
              style={{ borderRadius: '5px', padding: '10px' }}
            />
          )}
          {!!newQuery && (
            <TextInput
              type="text"
              value={newQuery.query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewQuery({ query: e.target.value, name: newQuery.name })
              }
              borderWidth="0px"
              multiline
              fullWidth
              InputLabelProps={{ shrink: true }}
              classes={{
                root: classes.newQueryInputRoot,
              }}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={hideDialog}>Cancel</Button>
        {!newQuery ? (
          <>
            <Button
              className={classes.deleteQueryButton}
              disabled={!selectedQuery}
              onClick={handleDeleteQuery}
            >
              Delete Query
            </Button>
            <Button type="primary" disabled={!selectedQuery} onClick={handleLoadQuery}>
              Load Query
            </Button>
          </>
        ) : (
          <Button
            type="primary"
            disabled={!newQuery.query || !newQuery.name}
            onClick={handleCreateQuery}
          >
            Create Query
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const useSavedQueriesStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    height: 650,
    padding: 0,
  },
  queryListScrollbar: {
    height: '100%',
    width: '35%',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
    overflowY: 'scroll',
  },
  queryItem: {
    width: '100%',
    padding: '10px 20px',
  },
  addQueryButton: {
    height: 50,
    width: '100%',
    borderRadius: 0,
  },
  selectedItem: {
    background: 'whitesmoke',
  },
  queryItemName: {
    color: 'black',
    fontSize: 16,
    fontWeight: 'bold',
    overflow: 'hidden',
    textWrap: 'nowrap',
    textOverflow: 'ellipsis',
  },
  queryItemDate: {
    color: 'gray',
    fontSize: 12,
  },
  queryDataContainer: {
    width: '65%',
    padding: 10,
  },
  deleteQueryButton: {
    background: 'rgb(198, 40, 40)',
    color: 'white',
    '&:hover': {
      background: 'rgb(178, 36, 36)',
    },
  },
  newQueryInputRoot: {
    height: '100%',
    backgroundColor: 'rgb(39, 40, 34)',
    borderRadius: 5,
    '& > .MuiInputBase-root': {
      height: '100% !important',
      padding: 15,
    },
    '& > div > .MuiInputBase-input': {
      height: '100% !important',
      padding: 0,
      color: 'rgb(249, 248, 245)',
    },
  },
}));

export default QuerySubtasks;
